'use client';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function ContactUsPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="/impact-dashboard-homepage" className="flex items-center space-x-3">
            <div className="relative w-10 h-10">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <path
                  d="M20 5C11.716 5 5 11.716 5 20C5 28.284 11.716 35 20 35C28.284 35 35 28.284 35 20C35 11.716 28.284 5 20 5Z"
                  fill="var(--color-warning)" />
                <path
                  d="M15 18C15 16.343 16.343 15 18 15C19.657 15 21 16.343 21 18C21 19.657 19.657 21 18 21C16.343 21 15 19.657 15 18Z"
                  fill="var(--color-primary)" />
                <path
                  d="M25 15C23.343 15 22 16.343 22 18C22 19.657 23.343 21 25 21C26.657 21 28 19.657 28 18C28 16.343 26.657 15 25 15Z"
                  fill="var(--color-primary)" />
                <path
                  d="M12 25C12 23.5 14 22 20 22C26 22 28 23.5 28 25V27H12V25Z"
                  fill="var(--color-primary)" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-headline font-bold leading-tight">Rhinos&Rangers</span>
              <span className="text-xs opacity-80 font-body">Conservation Intelligence</span>
            </div>
          </Link>
          <Link
            href="/impact-dashboard-homepage"
            className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300 flex items-center space-x-1">
            <Icon name="ArrowLeftIcon" size={16} variant="outline" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/5 py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
            <Icon name="EnvelopeIcon" size={32} variant="outline" className="text-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            Have a question, want to get involved, or need more information about our conservation work?
            We'd love to hear from you.
          </p>
        </div>
      </section>
      {/* Contact Card */}
      <section className="py-16 lg:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-xl shadow-organic border border-border p-8 lg:p-12 text-center">
            <h2 className="text-2xl font-headline font-bold text-foreground mb-3">
              Get in Touch
            </h2>
            <p className="text-muted-foreground font-body mb-8">
              The best way to reach the Rhinos&Rangers team is by email. We aim to respond to all
              enquiries within 2–3 business days.
            </p>

            <div className="bg-primary/5 rounded-lg p-6 mb-8 flex flex-col items-center space-y-3">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                <Icon name="EnvelopeIcon" size={24} variant="solid" className="text-primary" />
              </div>
              <p className="text-sm font-body text-muted-foreground uppercase tracking-wide font-semibold">
                Email Address
              </p>
              <a
                href="mailto:rhinosandranger@gmail.com"
                className="text-xl font-headline font-bold text-primary hover:underline break-all">
                rhinosandranger@gmail.com
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 text-left">
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="QuestionMarkCircleIcon" size={18} variant="outline" className="text-primary" />
                  <span className="text-sm font-headline font-semibold text-foreground">General Enquiries</span>
                </div>
                <p className="text-xs text-muted-foreground font-body">
                  Questions about our mission, programmes, or how to get involved.
                </p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="HeartIcon" size={18} variant="outline" className="text-primary" />
                  <span className="text-sm font-headline font-semibold text-foreground">Donations</span>
                </div>
                <p className="text-xs text-muted-foreground font-body">
                  Queries about donations, receipts, or corporate partnerships.
                </p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="NewspaperIcon" size={18} variant="outline" className="text-primary" />
                  <span className="text-sm font-headline font-semibold text-foreground">Media</span>
                </div>
                <p className="text-xs text-muted-foreground font-body">
                  Press enquiries, interviews, and media resources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-body opacity-80">
            &copy; {new Date()?.getFullYear()} Rhinos&Rangers Conservation Initiative. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
